import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  MenuItem,
  InputAdornment,
  FormHelperText,
  Select,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Box,
  Autocomplete,
} from "@mui/material";
import { Person, Email, Phone, AttachMoney } from "@mui/icons-material";
import FormDialog from "../Common/FormDialog";
import api from "../../utils/api";
import { Agent, Client, PropertyType } from "../../types";
import ToastNotification from "../../utils/toastNotification";

interface EditClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  client: Client | null;
}

interface ClientFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  budget: string;
  minBudget: string;
  maxBudget: string;
  propertyTypes: string[];
  transactionType: string;
  preferences: string;
  governorate: string[];
  district: string[];
  town: string;
  buildingAge: string;
  bedrooms: string;
  bathrooms: string;
  area: string;
  paymentMethod: string;
  downPayment: string;
  monthlyPayment: string;
  notes: string;
  agentIds: string[];
}

const EditClientDialog: React.FC<EditClientDialogProps> = ({
  open,
  onClose,
  onSuccess,
  client,
}) => {
  const [formData, setFormData] = useState<ClientFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    budget: "",
    minBudget: "",
    maxBudget: "",
    propertyTypes: [],
    transactionType: "",
    preferences: "",
    governorate: [],
    district: [],
    town: "",
    buildingAge: "",
    bedrooms: "",
    bathrooms: "",
    area: "",
    paymentMethod: "",
    downPayment: "",
    monthlyPayment: "",
    notes: "",
    agentIds: [],
  });
  const paymentMethods = [
    'CASH',
    'GOVERNMENT_HOUSING',
    'DOWN_PAYMENT_FACILITIES',
  ];
  const transactionTypes = ["RENT", "BUY"];

  // Lebanon governorates and districts - Complete official data
  const LEBANON_GOVERNORATES = [
    "Beirut",
    "Mount Lebanon",
    "North Lebanon",
    "South Lebanon",
    "Nabatieh",
    "Beqaa",
    "Baalbek-Hermel",
    "Akkar",
  ];

  // Map of Lebanese governorates (muhafazat) to their districts (aqdya)
  const DISTRICT_MAP: Record<string, string[]> = {
    Akkar: ["Akkar"],
    "Baalbek-Hermel": ["Baalbek", "Hermel"],
    Beirut: ["Beirut"],
    Beqaa: ["Zahle", "Western Beqaa", "Rashaya"],
    "Keserwan-Jbeil": ["Keserwan", "Jbeil"],
    "Mount Lebanon": ["Baabda", "Aley", "Chouf", "Matn"],
    "North Lebanon": [
      "Tripoli",
      "Batroun",
      "Bsharri",
      "Koura",
      "Miniyeh-Danniyeh",
      "Zgharta",
    ],
    "South Lebanon": ["Sidon", "Jezzine", "Tyre"],
    Nabatieh: ["Nabatieh", "Bint Jbeil", "Hasbaya", "Marjeyoun"],
  };

  const [districtOptions, setDistrictOptions] = useState<string[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadAgents();
      if (client) {
        const govArray: string[] = Array.isArray((client as any).governorate)
          ? (client as any).governorate
          : client.governorate ? [client.governorate] : [];
        const districts = govArray.flatMap((gov) => DISTRICT_MAP[gov] || []);
        setDistrictOptions(districts);

        setFormData({
          firstName: client.firstName,
          lastName: client.lastName,
          email: client.email || "",
          phone: client.phone,
          budget: client.budget ? client.budget.toString() : "",
          minBudget: client.minBudget ? client.minBudget.toString() : "",
          maxBudget: client.maxBudget ? client.maxBudget.toString() : "",
          propertyTypes: client.propertyTypes || [],
          transactionType: client.transactionType || "",
          preferences: client.preferences || "",
          governorate: Array.isArray((client as any).governorate)
            ? (client as any).governorate
            : client.governorate ? [client.governorate] : [],
          district: Array.isArray((client as any).district)
            ? (client as any).district
            : client.district ? [client.district] : [],
          town: client.town || "",
          buildingAge: client.buildingAge ? client.buildingAge.toString() : "",
          bedrooms: client.bedrooms ? client.bedrooms.toString() : "",
          bathrooms: client.bathrooms ? client.bathrooms.toString() : "",
          area: client.area ? client.area.toString() : "",
          paymentMethod: client.paymentMethod || "",
          downPayment: (client as any).downPayment?.toString() || "",
          monthlyPayment: (client as any).monthlyPayment?.toString() || "",
          notes: client.notes || "",
          agentIds: client.agents?.map((a) => a.id) || client.agentIds || [],
        });
      }
    }
  }, [open, client]);

  const loadAgents = async () => {
    try {
      const response = await api.get("/agents");
      setAgents(response.data);
    } catch (error) {
      console.error("Error loading agents:", error);
    }
  };

  const handleChange = (field: keyof ClientFormData, value: any) => {
    if (field === "governorate") {
      const selected: string[] = Array.isArray(value) ? value : (value ? [value] : []);
      const districts = selected.flatMap((gov) => DISTRICT_MAP[gov] || []);
      setDistrictOptions(districts);
      setFormData((prev) => ({ ...prev, governorate: selected, district: [] }));
      if (errors["governorate"])
        setErrors((prev) => ({ ...prev, governorate: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
      ToastNotification.error('Please enter first name');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      ToastNotification.error('Please enter last name');
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
      ToastNotification.error('Please enter a valid email address');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
      ToastNotification.error('Please enter phone number');
    }

    if (formData.budget && parseFloat(formData.budget) <= 0) {
      newErrors.budget = "Budget must be greater than 0";
      ToastNotification.error('Budget must be a positive number');
    }

    if (formData.minBudget && parseFloat(formData.minBudget) <= 0) {
      newErrors.minBudget = "Minimum budget must be greater than 0";
      ToastNotification.error('Minimum budget must be a positive number');
    }

    if (formData.maxBudget !== '' && parseFloat(formData.maxBudget) < 0) {
      newErrors.maxBudget = "Maximum budget cannot be negative";
      ToastNotification.error('Maximum budget cannot be negative');
    }

    if (
      formData.minBudget &&
      formData.maxBudget &&
      parseFloat(formData.minBudget) > parseFloat(formData.maxBudget)
    ) {
      newErrors.maxBudget =
        "Maximum budget must be greater than minimum budget";
      ToastNotification.error('Maximum budget cannot be less than minimum budget');
    }
    
    if (formData.bedrooms && parseInt(formData.bedrooms) < 0) {
      newErrors.bedrooms = 'Bedrooms cannot be negative';
      ToastNotification.error('Number of bedrooms cannot be negative');
    }
    
    if (formData.bathrooms && parseInt(formData.bathrooms) < 0) {
      newErrors.bathrooms = 'Bathrooms cannot be negative';
      ToastNotification.error('Number of bathrooms cannot be negative');
    }
    
    if (formData.area && parseFloat(formData.area) <= 0) {
      newErrors.area = 'Area must be greater than 0';
      ToastNotification.error('Area must be a positive number');
    }
    
    if (formData.buildingAge && parseInt(formData.buildingAge) < 0) {
      newErrors.buildingAge = 'Building age cannot be negative';
      ToastNotification.error('Building age cannot be negative');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !client) {
      if (validateForm() === false) {
        ToastNotification.warning('Please fill in all required fields correctly');
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        phone: formData.phone,
        budget: formData.budget ? parseFloat(formData.budget) : undefined,
        minBudget: formData.minBudget
          ? parseFloat(formData.minBudget)
          : undefined,
        maxBudget: formData.maxBudget
          ? parseFloat(formData.maxBudget)
          : undefined,
        propertyTypes:
          formData.propertyTypes.length > 0
            ? formData.propertyTypes
            : undefined,
        transactionType: formData.transactionType || undefined,
        preferences: formData.preferences || undefined,
        governorate: formData.governorate.length > 0 ? formData.governorate : undefined,
        district: formData.district.length > 0 ? formData.district : undefined,
        town: formData.town || undefined,
        buildingAge: formData.buildingAge
          ? parseInt(formData.buildingAge)
          : undefined,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
        bathrooms: formData.bathrooms
          ? parseInt(formData.bathrooms)
          : undefined,
        area: formData.area ? parseFloat(formData.area) : undefined,
        paymentMethod: formData.paymentMethod || undefined,
        downPayment: formData.downPayment ? parseFloat(formData.downPayment) : undefined,
        monthlyPayment: formData.monthlyPayment ? parseFloat(formData.monthlyPayment) : undefined,
        notes: formData.notes || undefined,
        agentIds: formData.agentIds.length > 0 ? formData.agentIds : undefined,
      };

      await api.put(`/clients/${client.id}`, payload);
      ToastNotification.success('Client updated successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error updating client:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update client';
      ToastNotification.error(errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset all form state
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      budget: "",
      minBudget: "",
      maxBudget: "",
      propertyTypes: [],
      transactionType: "",
      preferences: "",
      governorate: [],
      district: [],
      town: "",
      buildingAge: "",
      bedrooms: "",
      bathrooms: "",
      area: "",
      paymentMethod: "",
      downPayment: "",
      monthlyPayment: "",
      notes: "",
      agentIds: [],
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  return (
    <FormDialog
      open={open}
      title="Edit Client"
      subtitle="Update client information"
      onClose={handleClose}
      onSubmit={handleSubmit}
      maxWidth="md"
      submitText="Update Client"
      isSubmitting={isSubmitting}
      disableSubmit={isSubmitting}
    >
      <Grid container spacing={2.5}>
        {/* Personal Information */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            autoComplete="given-name"
            value={formData.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            value={formData.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            required
            placeholder="+961 XX XXX XXX"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Budget Range */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Min Budget (Optional)"
            type="number"
            value={formData.minBudget}
            onChange={(e) => handleChange("minBudget", e.target.value)}
            error={!!errors.minBudget}
            helperText={errors.minBudget || "Minimum budget"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Max Budget (Optional)"
            type="number"
            value={formData.maxBudget}
            onChange={(e) => handleChange("maxBudget", e.target.value)}
            error={!!errors.maxBudget}
            helperText={errors.maxBudget || "Maximum budget"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Property Types */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="property-types-label">
              Property Types (Optional)
            </InputLabel>
            <Select
              labelId="property-types-label"
              multiple
              value={formData.propertyTypes}
              onChange={(e) => handleChange("propertyTypes", e.target.value)}
              input={<OutlinedInput label="Property Types (Optional)" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {Object.values(PropertyType).map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox
                    checked={formData.propertyTypes.indexOf(type) > -1}
                  />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Transaction Type */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Transaction Type (Optional)"
            value={formData.transactionType}
            onChange={(e) => handleChange("transactionType", e.target.value)}
            helperText="Looking to rent or buy?"
          >
            <MenuItem value="">
              <em>Not specified</em>
            </MenuItem>
            {transactionTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Governorate */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            options={LEBANON_GOVERNORATES}
            value={formData.governorate}
            onChange={(_, val) => handleChange("governorate", val)}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                {option}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Governorate (Lebanon)"
                placeholder={formData.governorate.length === 0 ? 'Select Governorate(s)' : ''}
              />
            )}
          />
        </Grid>

        {/* District */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            options={districtOptions}
            value={formData.district}
            onChange={(_, val) => handleChange("district", val)}
            disableCloseOnSelect
            disabled={formData.governorate.length === 0}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                {option}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="District (Lebanon)"
                placeholder={
                  formData.governorate.length > 0
                    ? (formData.district.length === 0 ? 'Select District(s)' : '')
                    : 'Select Governorate first'
                }
              />
            )}
          />
        </Grid>

        {/* Town */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Town"
            value={formData.town}
            onChange={(e) => handleChange("town", e.target.value)}
            helperText="Enter the town or city name"
          />
        </Grid>

        {/* Assign Agents */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="agents-label">Assign Agents (Optional)</InputLabel>
            <Select
              labelId="agents-label"
              multiple
              value={formData.agentIds}
              onChange={(e) => handleChange("agentIds", e.target.value)}
              input={<OutlinedInput label="Assign Agents (Optional)" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const ag = agents.find((a) => a.id === id);
                    const name = ag?.user
                      ? `${ag.user.firstName} ${ag.user.lastName}`
                      : "Unknown Agent";
                    return <Chip key={id} label={name} size="small" />;
                  })}
                </Box>
              )}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  <Checkbox checked={formData.agentIds.indexOf(agent.id) > -1} />
                  <ListItemText
                    primary={
                      agent.user
                        ? `${agent.user.firstName} ${agent.user.lastName}`
                        : "Unknown Agent"
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Building Requirements */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Building Age (Years)"
            type="number"
            value={formData.buildingAge}
            onChange={(e) => handleChange("buildingAge", e.target.value)}
            helperText="Preferred age of building (e.g., new, 5 years old)"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Number of Bedrooms"
            type="number"
            value={formData.bedrooms}
            onChange={(e) => handleChange("bedrooms", e.target.value)}
            helperText="Desired number of bedrooms"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Number of Bathrooms"
            type="number"
            value={formData.bathrooms}
            onChange={(e) => handleChange("bathrooms", e.target.value)}
            helperText="Desired number of bathrooms"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Area (m²)"
            type="number"
            value={formData.area}
            onChange={(e) => handleChange("area", e.target.value)}
            helperText="Desired area in square meters"
          />
        </Grid>

        {/* Payment Method */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Payment Method (Optional)"
            value={formData.paymentMethod}
            onChange={(e) => handleChange("paymentMethod", e.target.value)}
            helperText="Preferred payment method"
          >
            <MenuItem value="">
              <em>Not specified</em>
            </MenuItem>
            {paymentMethods.map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {(formData.paymentMethod === 'Down Payment Facilities' || formData.paymentMethod === 'Government Housing') && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Down Payment Amount"
                type="number"
                value={formData.downPayment}
                onChange={(e) => handleChange("downPayment", e.target.value)}
                placeholder="e.g., 50000"
                helperText="Initial down payment amount"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monthly Payment"
                type="number"
                value={formData.monthlyPayment}
                onChange={(e) => handleChange("monthlyPayment", e.target.value)}
                placeholder="e.g., 1500"
                helperText="Monthly installment amount"
              />
            </Grid>
          </>
        )}

        {/* Preferences */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Preferences (Optional)"
            value={formData.preferences}
            onChange={(e) => handleChange("preferences", e.target.value)}
            multiline
            rows={3}
            helperText="Client preferences for property type, location, amenities, etc."
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            multiline
            rows={3}
            helperText="Additional notes about the client"
          />
        </Grid>

        {/* Submit Error */}
        {errors.submit && (
          <Grid item xs={12}>
            <FormHelperText error>{errors.submit}</FormHelperText>
          </Grid>
        )}
      </Grid>
    </FormDialog>
  );
};

export default EditClientDialog;
